import React, { useEffect, useState } from "react";
import UseContext from "../../../../../hooks/useContext";
import useModal from "../../../useModal";
import Modalheader from "../../../components/header";
import ModalBody from "../../../components/body";
import ModalFooter from "../../../components/footer";
import Button from "../../../../button";
import StyledInput from "../../../../styledInput/StyledInput";

const TITLES = {
  timeCut: "Calcul des coupures",
  breaks: "Pauses dans le fichier Peopulse",
  pdns: "Code PDNS",
  sunday: "Majoration du dimanche",
};

const BLOCK_DESCRIPTIONS = {
  breaks: "Comment les pauses figurent dans l’export Peopulse.",
  pdns: "Le code PDNS permet d’ajouter automatiquement une ligne de prime d’indemnité de petit déplacement pour chaque intérimaire.",
  sunday:
    "Une ligne avec le code 1205 est ajoutée dans l’export Peopulse avec le total des heures travaillées pour chaque dimanche.",
  timeCut:
    "Seuil en minutes : au‑delà, la pause est comptée comme une coupure.",
};

const ModalSettingsEditBlock = () => {
  const { modalData } = UseContext();
  const { toggle } = useModal();
  const block = modalData?.block;
  const hasValidBlock = Boolean(block && TITLES[block]);
  const isWaiting = Boolean(modalData?.isWaiting);

  const [draftTimeCut, setDraftTimeCut] = useState("");
  const [draftBreaks, setDraftBreaks] = useState([]);
  const [draftPdns, setDraftPdns] = useState(false);
  const [draftSunday, setDraftSunday] = useState(false);
  const [submittingTimeCut, setSubmittingTimeCut] = useState(false);
  const [submittingBreaks, setSubmittingBreaks] = useState(false);
  const [submittingPdns, setSubmittingPdns] = useState(false);
  const [submittingSunday, setSubmittingSunday] = useState(false);

  useEffect(() => {
    if (!hasValidBlock || block !== "timeCut" || isWaiting) return;
    const tc = modalData.timeCut;
    setDraftTimeCut(
      tc === "" || tc === undefined || tc === null ? "" : String(tc),
    );
  }, [
    hasValidBlock,
    block,
    isWaiting,
    modalData?.timeCut,
    modalData?.modalSessionKey,
  ]);

  useEffect(() => {
    if (!hasValidBlock || block !== "breaks" || isWaiting) return;
    if (!modalData?.breaks?.length) return;
    setDraftBreaks(modalData.breaks.map((b) => ({ ...b })));
  }, [
    hasValidBlock,
    block,
    isWaiting,
    modalData?.breaks,
    modalData?.modalSessionKey,
  ]);

  useEffect(() => {
    if (!hasValidBlock || block !== "pdns" || isWaiting) return;
    setDraftPdns(Boolean(modalData?.isPdns));
  }, [
    hasValidBlock,
    block,
    isWaiting,
    modalData?.isPdns,
    modalData?.modalSessionKey,
  ]);

  useEffect(() => {
    if (!hasValidBlock || block !== "sunday" || isWaiting) return;
    setDraftSunday(Boolean(modalData?.isSundayHours));
  }, [
    hasValidBlock,
    block,
    isWaiting,
    modalData?.isSundayHours,
    modalData?.modalSessionKey,
  ]);

  if (!hasValidBlock) {
    return null;
  }

  const handleConfirmTimeCut = async () => {
    if (submittingTimeCut) return;
    setSubmittingTimeCut(true);
    try {
      if (modalData.commitTimeCut) {
        await modalData.commitTimeCut(draftTimeCut);
      }
      toggle();
    } catch {
      /* Error already logged; modal stays open. */
    } finally {
      setSubmittingTimeCut(false);
    }
  };

  const handleConfirmBreaks = async () => {
    const selected = draftBreaks.find((b) => b.checked);
    if (!selected || !modalData.commitBreaks || submittingBreaks) return;
    setSubmittingBreaks(true);
    try {
      await modalData.commitBreaks(selected.name);
      toggle();
    } catch {
      /* Error already logged; modal stays open. */
    } finally {
      setSubmittingBreaks(false);
    }
  };

  const handleConfirmPdns = async () => {
    if (!modalData.commitPdns || submittingPdns) return;
    setSubmittingPdns(true);
    try {
      await modalData.commitPdns(draftPdns);
      toggle();
    } catch {
      /* Error already logged; modal stays open. */
    } finally {
      setSubmittingPdns(false);
    }
  };

  const handleConfirmSunday = async () => {
    if (!modalData.commitSundayHours || submittingSunday) return;
    setSubmittingSunday(true);
    try {
      await modalData.commitSundayHours(draftSunday);
      toggle();
    } catch {
      /* Error already logged; modal stays open. */
    } finally {
      setSubmittingSunday(false);
    }
  };

  const handleBreakSelectChange = (e) => {
    const value = e.target.value;
    setDraftBreaks((prev) =>
      prev.map((b) => ({
        ...b,
        checked: b.name === value,
      })),
    );
  };

  const renderBody = () => {
    if (isWaiting) {
      const fullWidthBlock =
        block === "timeCut" ||
        block === "breaks" ||
        block === "pdns" ||
        block === "sunday";
      return (
        <div
          className={`animate-pulse ${fullWidthBlock ? "w-full" : "max-w-[330px]"}`}
        >
          <div className="h-12 rounded bg-gray-200" />
        </div>
      );
    }

    switch (block) {
      case "timeCut":
        return (
          <div className="w-full">
            <StyledInput
              type="number"
              value={draftTimeCut}
              placeholder="0"
              unit="minutes"
              onChange={(e) => setDraftTimeCut(e.target.value)}
            />
          </div>
        );
      case "breaks": {
        if (draftBreaks.length === 0) {
          return (
            <div className="h-12 w-full animate-pulse rounded bg-gray-100" />
          );
        }
        const checkedBreak = draftBreaks.find((b) => b.checked);
        return (
          <div className="w-full">
            <StyledInput
              type="select"
              value={checkedBreak?.name ?? ""}
              list={draftBreaks.map((b) => ({
                value: b.name,
                label: b.title,
              }))}
              onChange={handleBreakSelectChange}
            />
          </div>
        );
      }
      case "pdns":
        return (
          <div
            className={`w-full rounded-none border p-[24px] ${draftPdns ? "border-workfully-700" : "border-gray-100"}`}
          >
            <StyledInput
              type="checkbox"
              label="Ajouter la ligne PDNS dans l’export Peopulse pour cet établissement"
              checked={draftPdns}
              onChange={(e) => setDraftPdns(e.target.checked)}
            />
          </div>
        );
      case "sunday":
        return (
          <div
            className={`w-full rounded-none border p-[24px] ${draftSunday ? "border-workfully-700" : "border-gray-100"}`}
          >
            <StyledInput
              type="checkbox"
              label="Ajouter la ligne 1205 pour les dimanches travaillés dans l’export Peopulse"
              checked={draftSunday}
              onChange={(e) => setDraftSunday(e.target.checked)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const headerDescription = BLOCK_DESCRIPTIONS[block];

  return (
    <>
      <Modalheader
        hide={toggle}
        hideShow={true}
        description={headerDescription}
      >
        {TITLES[block]}
      </Modalheader>
      <ModalBody>{renderBody()}</ModalBody>
      <ModalFooter>
        <Button size={"LG"} color={"SECONDARY"} onClick={toggle}>
          Fermer
        </Button>
        {!isWaiting && block === "timeCut" && (
          <Button
            size={"LG"}
            color={submittingTimeCut ? "DISABLED" : "PRIMARY"}
            disabled={submittingTimeCut}
            onClick={handleConfirmTimeCut}
          >
            {submittingTimeCut ? "Confirmation…" : "Confirmer"}
          </Button>
        )}
        {!isWaiting && block === "breaks" && (
          <Button
            size={"LG"}
            color={submittingBreaks ? "DISABLED" : "PRIMARY"}
            disabled={submittingBreaks}
            onClick={handleConfirmBreaks}
          >
            {submittingBreaks ? "Confirmation…" : "Confirmer"}
          </Button>
        )}
        {!isWaiting && block === "pdns" && (
          <Button
            size={"LG"}
            color={submittingPdns ? "DISABLED" : "PRIMARY"}
            disabled={submittingPdns}
            onClick={handleConfirmPdns}
          >
            {submittingPdns ? "Confirmation…" : "Confirmer"}
          </Button>
        )}
        {!isWaiting && block === "sunday" && (
          <Button
            size={"LG"}
            color={submittingSunday ? "DISABLED" : "PRIMARY"}
            disabled={submittingSunday}
            onClick={handleConfirmSunday}
          >
            {submittingSunday ? "Confirmation…" : "Confirmer"}
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalSettingsEditBlock;
